import React, { useState } from "react";
import { useSelector } from "react-redux";

import { getContentHeight, applyUnderline, renderWithBlanks } from "../../../../utils/textUtils";

import ArrowButton from "../widgets/ArrowButton";
import PageInfoBar from "../widgets/PageInfoBar";
import CorrectOverlay from "../widgets/CorrectOverlay";
import PassageAudioButton from "../widgets/PassageAudioButton";

/**
 * 문제 풀이 공용 틀
 * - 본문(paragraph)이 있으면: 데스크톱에서 좌(본문 고정) / 우(문제) 분할
 *   → 학생이 본문을 보면서 문제를 풉니다 (test-player 패턴)
 * - 본문이 없거나 모바일이면: 기존 단일 화면 + 본문 보기 버튼(모달)
 */
const CommonPage = ({
  children,
  handlePreviousPage,
  handleNextPage,
  currentIndex,
  currentPage,
  totalNo,
  fontFamily = '"Pretendard", sans-serif',
  paragraph = null,
  overlayVisible = false,
  isCorrect = null,
  handleSubmit = null,
  isMobile = false,
  accentColor = "#00A396", // [유형 톤] 본문 라벨 색 — 호출부에서 typeInfo 색 전달
}) => {
  const [passageOpen, setPassageOpen] = useState(true); // [모바일] 본문 기본 펼침 — 지문→문제 스크롤 (접기는 옵션)

  // [모바일 수정] 이 틀은 원래 isMobile prop을 받지만 호출부 다수가 안 넘겨서
  // 폰에서도 데스크톱 분할(본문 40% 패널)이 그려졌다 — 모의고사·내용이해가
  // 한 글자씩 세로로 붕괴한 원인. 훅으로 자체 감지해 prop과 OR로 판단한다.
  // [분할 판정 v2] 기준은 오직 '지금 창 폭'.
  // - 폭 > 768px(창을 반쯤 줄인 PC 포함 대부분의 PC) → 좌 본문 / 우 문제 분할 유지
  // - 폭 ≤ 768px(실제 폰) → 분할 대신 접이식 본문 카드(아래)
  // redux ui.mode는 과거 방문 상태가 남아 PC에서 오판할 수 있어 배제.
  const [narrow, setNarrow] = useState(
    typeof window !== "undefined" && Math.min(window.innerWidth, window.screen.width) <= 768
  );
  React.useEffect(() => {
    const f = () => setNarrow(Math.min(window.innerWidth, window.screen.width) <= 768);
    window.addEventListener("resize", f);
    window.addEventListener("orientationchange", f);
    return () => {
      window.removeEventListener("resize", f);
      window.removeEventListener("orientationchange", f);
    };
  }, []);
  const mobile = isMobile || narrow;

  const showSplit = paragraph !== null && !mobile;
  // 내용이해 지문 낭독 음원. 출제할 때 섹션 question 에 실어 둔다(api assignment views 의 passage_audio_url_of).
  const passageAudioUrl = useSelector((state) => state.assignment?.section?.question?.audio_url) || "";

  return (
    <div
      className="common-page"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "stretch",
        // [제출 버튼] 이 틀이 기준이 되어야 한다. relative 가 없으면 아래 제출하기
        // 버튼의 absolute 가 화면 전체(LoadingOverlay 의 relative)를 기준으로 잡혀
        // 본문을 벗어나 화면 오른쪽 아래 구석에 붙는다.
        position: "relative",
        height: getContentHeight(mobile),
        // [중앙 정렬] 좌우 비대칭 패딩(8/12, 30/40)이 전 화면을 왼쪽으로
        // 치우치게 하던 것 → 대칭화
        paddingRight: mobile ? "10px" : "35px",
        paddingLeft: mobile ? "10px" : "35px",
        // [중앙 정렬] 와이드 모니터에서 본문 패널(최대 560px 고정)+문제 열이
        // 화면 왼쪽에 몰리던 것 → 분할 화면 전체 폭을 제한하고 가운데 고정
        width: "100%",
        maxWidth: showSplit ? "1250px" : "none",
        marginLeft: "auto",
        marginRight: "auto",
        boxSizing: "border-box",
        fontFamily: fontFamily,
        backgroundColor: "#FFFFFF",
        // [최종 백신 ③] 1px라도 가로로 넘치면 iOS가 레이아웃 뷰포트를 부풀려
        // innerWidth가 뻥튀기되고(776 등) 모바일 판정이 전부 PC로 뒤집히는
        // 악순환이 시작된다 → 공용틀에서 가로 넘침 원천 차단.
        overflowX: "hidden",
        gap: showSplit ? "26px" : "0",
      }}
    >
      {/* 본문 패널 (데스크톱 · 본문이 있을 때) */}
      {showSplit && (
        <div
          className="common-page__passage"
          style={{
            flex: "0 0 40%",
            maxWidth: "560px",
            display: "flex",
            flexDirection: "column",
            alignSelf: "center", // [v13] 문제 열과 같은 세로 중앙 기준 — 천장 붙음 해소
            maxHeight: "calc(100% - 44px)", // 긴 지문은 여기까지, 내부 스크롤
            margin: "22px 0",
            backgroundColor: "#FAFCFC",
            border: "1px solid #E6EDEC",
            borderRadius: "16px",
            overflow: "hidden",
          }}
        >
          <div
            className="common-page__passage-head"
            style={{
              padding: "13px 20px 11px",
              borderBottom: "1px solid #E6EDEC",
              fontSize: "12px",
              fontWeight: 800,
              letterSpacing: "0.08em",
              color: accentColor,
              backgroundColor: "#FFFFFF",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            전체 본문
            <PassageAudioButton audioUrl={passageAudioUrl} accentColor={accentColor} />
          </div>
          <div
            className="common-page__passage-body"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "18px 22px",
              fontSize: "16px",
              lineHeight: 1.9,
              color: "#0E141B",
              letterSpacing: "-0.01em",
              wordBreak: "keep-all",
              // [PC 지문 가운데 v8] 짧은 지문이 천장에 붙는 것 → 내부 flex 세로 가운데
              display: "flex",
              flexDirection: "column",
              // **safe 를 빼면 긴 지문의 앞부분이 잘려서 안 보인다.** 내용이 상자보다
              // 길 때 그냥 center 로 두면 넘친 만큼 위아래로 밀려 나가는데, 위로 밀린
              // 부분은 스크롤로 닿을 수가 없다 (2026-09-16 광주 나현아 신고).
              // safe 는 넘칠 때만 가운데 정렬을 포기하고 위에서부터 쌓는다.
              justifyContent: "safe center",
              minHeight: "100%",
            }}
          >
            {/* 한 줄은 반드시 <span> 하나로 감싼다. 이 상자가 flex column 이라,
                감싸지 않으면 밑줄 마커(`--낱말--`)로 쪼개진 조각 하나하나가 flex
                아이템이 되어 낱말마다 줄이 바뀐다 (2026-09-13 모의고사 신유형에서
                드러났다. 마커가 없던 기존 지문은 조각이 하나뿐이라 멀쩡했다). */}
            {paragraph.split("\n").map((line, index) => (
              <React.Fragment key={index}>
                <span>{renderWithBlanks(line)}</span>
                <br />
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* 문제 영역 */}
      <div
        style={{
          flex: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "space-between",
          // [여백 수술] 모바일 화살표 오버레이의 기준점
          position: mobile ? "relative" : "static",
        }}
      >
        {/* [화살표 v3] 모바일 오버레이 제거 — 하단 카운터 양옆으로 이동. PC는 기존 그대로 */}
        {!mobile && (
          <div>
            <ArrowButton
              direction="left"
              onClick={handlePreviousPage}
              visibleCondition={currentIndex > 0}
            />
          </div>
        )}
        {/* [긴 문장 대응] children 전폭 보장 + 모바일 세로 스크롤.
            폭 기준이 없으면 자식의 width:100%가 좁은 기둥으로 수축해
            한 단어씩 세로로 떨어졌고(어법·해설), 내용이 길면 세로 중앙정렬이
            위로 넘쳐 상단바를 침범했다. */}
        <div
          style={{
            flex: 1,
            minWidth: 0,
            height: "100%",
            display: "flex",
            // [펼침 세로 넘침] row 방향 alignItems:center = 세로 중앙정렬 →
            // 본문 펼쳐 내용이 컨테이너보다 길어지면 위쪽이 스크롤 불가로 잘려
            // 상단바 밑에 숨었다. 짧은 내용 중앙은 안쪽 margin:auto 0이 담당.
            alignItems: mobile ? "flex-start" : "safe center",
            // [위 여백의 진짜 범인] 스크롤 컨테이너의 세로 중앙정렬은 내용이
            // 길어지면 위 넘침·위 패딩을 스크롤 불가 영역으로 잘라버린다(CSS 함정).
            // 짧은 내용의 중앙정렬은 안쪽 margin:"auto 0"가 이미 담당 →
            // 모바일은 flex-start로 위 여백 부활.
            // PC 도 safe 를 붙인다. 내용이 길 때 가운데로 맞추면 위쪽이 화면 밖으로
            // 밀려 상단바 밑에 숨는다. safe 는 넘칠 때만 위에서부터 쌓는다.
            justifyContent: mobile ? "flex-start" : "safe center",
            overflowY: mobile ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
            padding: mobile ? "12px 0 64px" : 0, // [v8] 고정 지문이라 여백 일정 // [여백 v6] 본문 "있는" 유형의 펼침에만 위 여백 축소 — 단어류 세로 중앙 복원
          }}
        >
          <div style={{ width: "100%", margin: (mobile && paragraph !== null) ? "0" : "auto 0" }}>
            {/* [모바일 지문 v8] 접기 제거 — 지문 고정 표시 + 문제만 스크롤 */}
            {mobile && paragraph !== null && (
              <div
                className="common-page__passage"
                style={{
                  border: "1px solid #E6EDEC",
                  borderRadius: "16px", // [v10] PC 패널과 동일 둥근 모서리
                  background: "#FAFCFC",
                  marginBottom: "12px",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                  // [고정 지문] 최대 높이 40vh — 넘으면 지문 내부만 스크롤
                  maxHeight: "40vh",
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <div
                  className="common-page__passage-head"
                  style={{
                    padding: "10px 12px 8px",
                    borderBottom: "1px solid #E6EDEC",
                    fontSize: "11px",
                    fontWeight: 800,
                    letterSpacing: "0.08em",
                    color: accentColor,
                    backgroundColor: "transparent",
                    flexShrink: 0,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: "8px",
                  }}
                >
                  전체 본문
                  <PassageAudioButton audioUrl={passageAudioUrl} accentColor={accentColor} />
                </div>
                <div
                  className="common-page__passage-body"
                  style={{
                    overflowY: "auto",
                    WebkitOverflowScrolling: "touch",
                    padding: "12px 14px",
                    fontSize: "14px",
                    lineHeight: 1.85,
                    color: "#0E141B",
                    wordBreak: "keep-all",
                    overflowWrap: "break-word",
                  }}
                >
                  {paragraph.split("\n").map((line, index) => (
                    <React.Fragment key={index}>
                      <span>{renderWithBlanks(line)}</span>
                      <br />
                    </React.Fragment>
                  ))}
                </div>
              </div>
            )}
            {children}
            {/* [제출 인라인] 모바일: 둥둥 뜨는 버튼 대신 스크롤 끝에 자연 배치
                — "답 입력하고 스크롤 내리면 제출이 있는" 흐름 */}
            {mobile && handleSubmit !== null && (
              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "18px" }}>
                <button
                  className="common-page__submit"
                  style={{
                    padding: "8px 22px", // [제출 v2] 소형화
                    backgroundColor: currentPage.isSubmitted ? "#E6EDEC" : "#0E141B",
                    color: currentPage.isSubmitted === true ? "#737373" : "white",
                    border: "none",
                    fontSize: "14px",
                    fontWeight: 700,
                    fontFamily: "inherit",
                    borderRadius: "999px",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSubmit()}
                >
                  제출하기
                </button>
              </div>
            )}
          </div>
        </div>
        {!mobile && (
          <div>
            <ArrowButton
              direction="right"
              onClick={handleNextPage}
              visibleCondition={true}
            />
          </div>
        )}
      </div>

      <PageInfoBar currentNo={currentIndex + 1} totalNo={totalNo} />

      {/* [화살표 v4] 모바일: 하단 고정 카운터바 '위에 겹치는' 오버레이로 양옆 배치.
          v3처럼 흐름 속에 두면 카운터바(position fixed)만 하단으로 빠지고
          화살표 쌍이 본문 옆에 남아 콘텐츠를 밀어냈다(우측 잘림·둥둥 화살표). */}
      {mobile && (
        <div
          style={{
            position: "fixed",
            bottom: 0,
            left: "10%",
            width: "80%",
            height: "40px", // [화살표 v6] 모바일 카운터 바(40px)와 동일 — 숫자와 세로 정렬
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            zIndex: 6,
            pointerEvents: "none",
          }}
        >
          <div style={{ pointerEvents: "auto", width: "44px", display: "flex", justifyContent: "center" }}>
            <ArrowButton
              direction="left"
              onClick={handlePreviousPage}
              visibleCondition={currentIndex > 0}
            />
          </div>
          <div style={{ pointerEvents: "auto", width: "44px", display: "flex", justifyContent: "center" }}>
            <ArrowButton
              direction="right"
              onClick={handleNextPage}
              visibleCondition={true}
            />
          </div>
        </div>
      )}

      {/* [교체] 부유 '본문 보기' 버튼 → 위의 접이식 본문 카드로 대체됨 */}

      <div
        style={{
          // 화면 전체를 덮는 연출이라 fixed 여야 한다. absolute 로 두면 100vh 짜리
          // 상자가 본문 안에 끼어 그만큼 세로로 넘치고, 아무것도 안 보이는데
          // 스크롤바만 생긴다(보이지 않아도 넘침에는 포함된다).
          position: "fixed",
          top: 0,
          left: 0,
          width: "100vw",
          height: "100vh",
          opacity: overlayVisible ? 1 : 0, // Fade-in/out
          visibility: overlayVisible ? "visible" : "hidden", // DOM에서 숨김
          transition: "opacity 200ms ease-out, visibility 200ms",
        }}
      >
        <CorrectOverlay bool={isCorrect} />
      </div>
      {!mobile && handleSubmit !== null && (
        <button
          className="common-page__submit"
          style={{
            position: "absolute", // 부모(80% width) 안에서 절대 위치
            // [모바일 수정] 폰에서 본문/보기와 겹치던 위치 → 우하단 축소 이동
            bottom: mobile ? "76px" : "80px",
            right: mobile ? "14px" : "60px",
            padding: mobile ? "8px 22px" : "10px 35px",
            backgroundColor: currentPage.isSubmitted ? "#E6EDEC" : "#0E141B",
            color: currentPage.isSubmitted === true ? "#737373" : "white",
            border: "none",
            fontSize: mobile ? "14px" : "16px",
            fontWeight: 700,
            fontFamily: "inherit",
            borderRadius: "999px",
            cursor: "pointer",
            transition: "background-color .12s",
            // [제출 버튼] 콘텐츠와 층 분리 — 떠 있는 버튼임을 그림자로 명시
            boxShadow: "0 6px 18px rgba(14,20,27,.28)",
            zIndex: 7,
          }}
          onMouseEnter={(e) => {
            if (!currentPage.isSubmitted)
              e.currentTarget.style.backgroundColor = "#00A396";
          }}
          onMouseLeave={(e) => {
            if (!currentPage.isSubmitted)
              e.currentTarget.style.backgroundColor = "#0E141B";
          }}
          onClick={() => handleSubmit()}
        >
          제출하기
        </button>
      )}
    </div>
  );
};

export default CommonPage;
